import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import {
	IAddAuthorizedMemberRequestData,
	IAddAuthorizedMemberResponseData,
	IAddBlacklistVehicleRequestData,
	IAddVehicleRequestData,
	IAddVehicleResponseData,
	IEditAuthorizedMemberRequestData,
	IGetAuthorizedListRequestData,
	IGetAuthorizedListResponse,
	IGetBlacklistRequestData,
	IGetBlacklistResponse,
	IGetBlacklistResponseData,
	IGetVehiclesListResponseData,
	IUploadAuthorizedVehiclesExcelRequestData,
	IUploadAuthorizedVehiclesExcelResponseData,
	IUploadBlacklistVehiclesExcelRequestData,
	IUploadBlacklistVehiclesExcelResponseData
} from "../../interfaces";
import { queries } from "./queries.reducer";

export const dataManagementApi = queries.injectEndpoints({
	endpoints: (builder) => ({
		// GET BLACKLIST
		getBlacklist: builder.query<IGetBlacklistResponse, IGetBlacklistRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehicles",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: { ...requestData, auth_type: "blacklist" }
			})
		}),

		// DELETE BLACKLIST VEHICLE
		deleteBlacklistVehicle: builder.mutation<void, number>({
			query: (id) => ({
				url: `/analytics/vehicles/${id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ADD BLACKLIST VEHICLE
		addBlacklistVehicle: builder.mutation<IGetBlacklistResponseData, IAddBlacklistVehicleRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehicles",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPLOAD BLACKLIST VEHICLES
		uploadBlacklistData: builder.mutation<void, FormData>({
			query: (requestData) => ({
				url: "/analytics/blacklist_vehicles/upload",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			})
		}),

		// GET AUTHORIZED LIST
		getAuthorizedList: builder.query<IGetAuthorizedListResponse, IGetAuthorizedListRequestData>({
			query: (requestData) => ({
				url: "/analytics/owner",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// DELETE MEMBER VEHICLE
		deleteVehicle: builder.mutation<void, number>({
			query: (id) => ({
				url: `/analytics/vehicles/${id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ADD MEMBER VEHICLE
		addVehicle: builder.mutation<IAddVehicleResponseData, IAddVehicleRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehicles",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// EDIT AUTHORIZED MEMBER
		editAuthorizedMember: builder.mutation<void, IEditAuthorizedMemberRequestData>({
			query: (requestData) => ({
				url: `/analytics/vehicleowner/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ADD AUTHORIZED MEMBER
		addAuthorizedMember: builder.mutation<IAddAuthorizedMemberResponseData, IAddAuthorizedMemberRequestData>({
			query: (requestData) => ({
				url: "/analytics/vehicleowner",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPLOAD AUTHORIZED VEHICLES
		uploadAuthorizedListData: builder.mutation<void, FormData>({
			query: (requestData) => ({
				url: "/analytics/excel/upload",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			})
		}),

		// GET ALL VEHICLES LIST
		getVehiclesList: builder.query<IGetVehiclesListResponseData, void>({
			query: () => ({
				url: "/analytics/vehicles",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: { auth_type: "whitelist" }
			})
		}),

		// DELETE AUTHORIZED MEMBER
		deleteMember: builder.mutation<void, number>({
			query: (id) => ({
				url: `/analytics/vehicleowner/${id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET ALL BLACKLISTED VEHICLES LIST
		getAllBlacklistedVehicles: builder.query<string[], void | null>({
			query: () => ({
				url: "/analytics/get_blacklisted_number_plates",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// UPLOAD AUTHORIZED VEHICLES EXCEL
		uploadAuthorizedVehiclesData: builder.mutation<
			IUploadAuthorizedVehiclesExcelResponseData,
			IUploadAuthorizedVehiclesExcelRequestData
		>({
			query: (requestData) => ({
				url: "/analytics/upload_authorized_vehicles",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: (response) => {
				if (response && Array.isArray(response.data)) {
					response.data = {
						data: response.data,
						message: "Failed to upload data"
					};
				}

				return response;
			}
		}),

		// UPLOAD BLACKLIST VEHICLES EXCEL
		uploadBlacklistVehiclesData: builder.mutation<
			IUploadBlacklistVehiclesExcelResponseData,
			IUploadBlacklistVehiclesExcelRequestData
		>({
			query: (requestData) => ({
				url: "/analytics/upload_blacklisted_vehicles",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			})
		})
	})
});

export const {
	useGetBlacklistQuery,
	useDeleteBlacklistVehicleMutation,
	useAddBlacklistVehicleMutation,
	useUploadBlacklistDataMutation,
	useGetAuthorizedListQuery,
	useDeleteVehicleMutation,
	useAddVehicleMutation,
	useEditAuthorizedMemberMutation,
	useAddAuthorizedMemberMutation,
	useUploadAuthorizedListDataMutation,
	useGetVehiclesListQuery,
	useDeleteMemberMutation,
	useGetAllBlacklistedVehiclesQuery,
	useUploadAuthorizedVehiclesDataMutation,
	useUploadBlacklistVehiclesDataMutation
} = dataManagementApi;
